import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { EVENT_MANAGER_PLUGINS } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { RouteReuseStrategy } from '@angular/router';
import { Overlay } from '@angular/cdk/overlay';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { NgxSpinnerModule } from 'ngx-spinner';
import { Angulartics2Module } from 'angulartics2';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from 'src/environments/environment';
import { TBS_DATE_CONFIG } from '@ngx-terabase/ui/i18n';
import { DefaultMomentAdapter, ALLOWED_IMAGES_FORMATS, ALLOWED_DOCUMENTS_FORMATS } from './injection-tokens';
import { MomentDateAdapter } from './moment-date-adapter';
import { ProjectAwareRouteReuseStrategy } from './app-route-reuse.strategy';
import { authenticationInterceptorFn } from './@core/services/interceptors/authentication.interceptor';
import { httpErrorInterceptorFn } from './@core/services/interceptors/http-error.interceptor';
import { cancelPendingRequestsInterceptor } from './@core/services/interceptors/cancel-pending-requests.interceptor';
import { AuthenticationService } from './@core/services/rest/authentication.service';
import { IoTClientService } from './@core/services/common/iot-client.service';
import { routes } from './app.routes';
import { provideAmplify } from './amplify.setup';
import { provideAgGrid } from './ag-grid.setup';
import { provideSentry } from './sentry.setup';
import { NgxEchartsModule } from 'ngx-echarts';
import { provideMarkdown } from 'ngx-markdown';
import { ThrottleEventPlugin } from './shared/event-plugins/throttle-event.plugin';
import { DebounceEventPlugin } from './shared/event-plugins/debounce-event.plugin';
export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideMarkdown(),
    provideAnimationsAsync(),
    provideAmplify(),
    provideAgGrid(),
    provideSentry(),
    provideHttpClient(
      withInterceptors([authenticationInterceptorFn, cancelPendingRequestsInterceptor, httpErrorInterceptorFn]),
    ),
    provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),
    provideAppInitializer(() => inject(AuthenticationService).initialize()),
    provideAppInitializer(() => inject(IoTClientService).initialize()),
    importProvidersFrom(
      NgxSpinnerModule,
      NgxEchartsModule.forRoot({ echarts: () => import('./echarts.setup').then((m) => m.echarts) }),
      Angulartics2Module.forRoot({ pageTracking: { clearIds: true, clearQueryParams: true, clearHash: true } }),
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: ['development', 'development-test', 'staging', 'production'].includes(environment.name),
        registrationStrategy: 'registerWhenStable:30000',
      }),
    ),
    { provide: EVENT_MANAGER_PLUGINS, useClass: ThrottleEventPlugin, multi: true },
    { provide: EVENT_MANAGER_PLUGINS, useClass: DebounceEventPlugin, multi: true },
    { provide: ALLOWED_IMAGES_FORMATS, useValue: ['png', 'jpg', 'jpeg'] },
    {
      provide: ALLOWED_DOCUMENTS_FORMATS,
      useValue: ['pdf', 'doc', 'docx', 'txt', 'csv', 'xls', 'xlsx', 'xlsm', 'json'],
    },
    { provide: LOCALE_ID, useValue: 'en-US' },
    { provide: TBS_DATE_CONFIG, useValue: { firstDayOfWeek: 1 } },
    { provide: DefaultMomentAdapter, useClass: MomentDateAdapter },
    { provide: RouteReuseStrategy, useClass: ProjectAwareRouteReuseStrategy },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useFactory: (overlay: Overlay) => ({ scrollStrategy: overlay.scrollStrategies.noop() }),
      deps: [Overlay],
    },
  ],
};
